import { useCallback, useMemo } from 'react';

import axios from 'axios';

import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { useNetwork, useVaultToken } from 'hooks';

/**
 * Every eVault endpoint, in one place.
 *
 * Not added to `constant/api/api-url.tsx` because only this deck speaks to
 * them and only with the scoped bearer below — a shared constant would invite a
 * caller to reach them with the session token, which esign.api cannot verify.
 * `/vault-tokens` and `/evault-addons` are the exception and already live in
 * API_URL: they are clients.api surfaces and take the ordinary session.
 */
export const EVAULT_API = {
	VAULTS: '/vaults',
	VAULT_DOCUMENTS: '/vault-documents',
	VAULT_TRANSFERS: '/vault-transfers',
	VAULT_ENTITLEMENT: '/vault-entitlement',
	VAULT_AUDIT: '/vault-audit',
	VAULT_EVIDENCE: '/vault-evidence',
	VAULT_CERTIFICATES: '/vault-certificates',
	VAULT_PRODUCTIONS: '/vault-productions',
	VAULT_DISPOSITIONS: '/vault-dispositions',
	VAULT_COPIES: '/vault-copies',
	VAULT_REGISTRY: '/vault-registry',
	/** SIM-23 — taking an original out of the vault's custody for good. */
	VAULT_CHECKOUTS: '/vault-checkouts',
	/** SIM-23 — depositing a checked-out original again, as a new linked record. */
	VAULT_REVAULTS: '/vault-revaults',
	/** §10 — the schedule catalogue for the vault form's record-category selector. */
	VAULT_RETENTION_SCHEDULES: '/vault-retention-schedules',
};

const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * The five outcomes the deck actually renders differently.
 *
 * Modelled as a union rather than `(data, error)` because each failure has its
 * own SCREEN: `unentitled` opens the upsell (never a toast — a paywall is not an
 * error), `conflict` and `invalid` print the server's own sentence inline in the
 * form that caused them (those messages are specific by design — "not a signer
 * on 2 of the selected documents" — and a generic fallback would hide the
 * reason), and only `error` gets the retry band.
 */
export type IEvaultOutcome<T> =
	| { kind: 'ok'; data: T }
	| { kind: 'unentitled'; message: string; status?: number }
	| { kind: 'conflict'; message: string; status?: number }
	| { kind: 'invalid'; message: string; status?: number }
	| { kind: 'error'; message: string; status?: number };

const messageOf = (body: unknown): string => {
	const message = (body as { message?: unknown } | null)?.message;
	return typeof message === 'string' && message.trim() ? message : GENERIC_ERROR;
};

/**
 * Status → outcome. A 403 that is NOT the entitlement refusal (e.g. "You can
 * only transfer documents you currently own") deliberately falls through to
 * `error`: opening a billing upsell over an ownership failure would be a lie
 * about what went wrong.
 *
 * `status` rides along on every failure because one caller has to tell a 404
 * apart from every other refusal: the vault page renders "This vault isn't
 * here" for one and a retry band for the rest, and matching on the message text
 * would make that screen depend on the server's wording rather than its
 * contract.
 */
const mapFailure = (status: number, message: string): IEvaultOutcome<never> => {
	if (status === 403 && message.includes('EVAULT_NOT_ENTITLED')) {
		return { kind: 'unentitled', message, status };
	}
	if (status === 409) return { kind: 'conflict', message, status };
	if (status === 400) return { kind: 'invalid', message, status };
	return { kind: 'error', message, status };
};

/** Xitkov wraps every body as `{message:'ok', data}`; the deck wants `data`. */
const unwrap = <T,>(payload: unknown): T => {
	const wrapped = (payload as { data?: unknown } | null)?.data;
	return (wrapped ?? payload) as T;
};

export interface IEvaultApi {
	get: <T>(url: string) => Promise<IEvaultOutcome<T>>;
	post: <T>(url: string, body: unknown) => Promise<IEvaultOutcome<T>>;
	patch: <T>(url: string, body: unknown) => Promise<IEvaultOutcome<T>>;
	/** Empty until the first mint resolves — hold the surface back until it is set. */
	vaultToken: string;
	vaultTokenError: string;
	/** Force a fresh mint — e.g. right after the add-on itself changed. */
	remintToken: () => Promise<void>;
}

/**
 * The eVault transport: `useNetwork`, plus the scoped bearer, plus a status
 * mapping.
 *
 * WHY A WRAPPER AT ALL. esign.api cannot authenticate users, so its endpoints
 * take a short-lived `evault`-scoped JWT that clients.api mints from a verified
 * session (`useVaultToken`, shared with the send-envelope modal). That token is
 * passed per call. It is deliberately NOT written into `WebTokenState`, which
 * would swap the bearer for every request in the app.
 *
 * WHY `patch` GOES THROUGH AXIOS AND THE OTHER TWO DO NOT. With
 * `returnResponse: true`, `useNetwork.get`/`post` hand back `{errorCode,
 * errorData}` from the axios error and — importantly — return it BEFORE
 * reaching `errorNotification`, so a 403 or a 409 arrives here with its status
 * intact and raises no toast. `useNetwork.patch` is a different animal: it is
 * built on `fetch`, which does not reject on a non-2xx, and it returns
 * `apiData.data ?? apiData` — the STATUS IS DISCARDED before the caller sees
 * anything. There is no argument to it that changes that, so a 409 would be
 * indistinguishable from a 200 whose body happened to carry a message. Rather
 * than guess from the body shape on the one verb where the distinction is
 * load-bearing (rename clash, transfer replay), patch is issued with axios
 * against the same host and mapped by real status.
 */
export const useEvaultApi = (): IEvaultApi => {
	const { vaultToken, vaultTokenError, remint } = useVaultToken();
	const {
		get: netGet,
		post: netPost,
		sandbox,
	} = useNetwork({ updateState: false, returnResponse: true });

	/*
	 * The app's own headers, with the bearer swapped. `accountMode` is what
	 * useNetwork sends on every other request and the proxy forwards it
	 * untouched, so dropping it here would make eVault the one surface that
	 * ignores sandbox mode.
	 */
	const headers = useMemo(
		() => ({
			Authorization: `Bearer ${vaultToken}`,
			accountMode: sandbox ? 'sandbox' : 'live',
			'Content-Type': 'application/json',
			'Cache-Control': 'no-cache',
		}),
		[vaultToken, sandbox]
	);

	const get = useCallback(
		async <T,>(url: string): Promise<IEvaultOutcome<T>> => {
			/* `useNetwork.get`'s second argument REPLACES the whole axios config
			   (`configHeader ?? config`) rather than merging into it — unlike
			   `post`, which takes a flat headers object. Hence the wrapper. */
			const result = await netGet(url, { headers });
			if (result?.errorCode) {
				return mapFailure(Number(result.errorCode), messageOf(result.errorData));
			}
			return { kind: 'ok', data: unwrap<T>(result?.apiPayload) };
		},
		[netGet, headers]
	);

	const post = useCallback(
		async <T,>(url: string, body: unknown): Promise<IEvaultOutcome<T>> => {
			/* Flat headers here: `post` merges its third argument into
			   `postConfig.headers`. It also already unwraps `data` on success. */
			const result = await netPost(url, body, headers);
			if (result?.errorCode) {
				return mapFailure(Number(result.errorCode), messageOf(result.errorData));
			}
			if (result === null || result === undefined) {
				return { kind: 'error', message: GENERIC_ERROR };
			}
			return { kind: 'ok', data: result as T };
		},
		[netPost, headers]
	);

	const patch = useCallback(
		async <T,>(url: string, body: unknown): Promise<IEvaultOutcome<T>> => {
			try {
				const response = await axios.patch(API_HOST + url, body, { headers });
				return { kind: 'ok', data: unwrap<T>(response?.data) };
			} catch (err: unknown) {
				const failed = (err as { response?: { status?: number; data?: unknown } })
					?.response;
				return mapFailure(Number(failed?.status ?? 500), messageOf(failed?.data));
			}
		},
		[headers]
	);

	/* Memoised: consumers put this object in effect dependency arrays, and a
	   fresh literal every render would refetch the drive on every keystroke. */
	return useMemo(
		() => ({ get, post, patch, vaultToken, vaultTokenError, remintToken: remint }),
		[get, post, patch, vaultToken, vaultTokenError, remint]
	);
};

/**
 * The query builder, ported from the component's `toQuery`: drops nulls, joins
 * arrays with a comma, and skips empty or whitespace-only strings — so an
 * untouched filter never reaches the server as `&search=` and narrows a result
 * set to nothing.
 */
export const toQuery = (
	params: Record<string, string | string[] | null | undefined>
): string => {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value === null || value === undefined) return;
		if (Array.isArray(value)) {
			if (value.length) search.append(key, value.join(','));
			return;
		}
		if (value.trim()) search.append(key, value);
	});
	const query = search.toString();
	return query ? `?${query}` : '';
};
